/**
 * @file BadgeLabelsGroup.tsx
 * @description Группа «Подписи бейджей» на вкладке «Карточка товара»
 * (phase19.16 Р2а; uiux-design-r2a §2; SB65; SB122 — группа-карточка,
 * свёрнута по умолчанию, сводка «по умолчанию» / «N своих», ✕ при
 * недопустимой подписи): своя подпись для «бестселлер», «новинки»,
 * «распродажа», «скоро в продаже». Пусто — подпись платформы. Проверка
 * `badgeLabelIssue` из shared (та же, что у сервера): «№1», «лучший»,
 * «самый», «топ»… нельзя — текст причины под полем по-русски, в черновик
 * уходит только допустимая подпись.
 */

import {
  BADGE_LABEL_MAX,
  BADGE_LABEL_STOP_WORDS,
  CATALOG_BADGE_DEFAULT_LABELS,
  CATALOG_BADGE_KEYS,
  badgeLabelIssue,
  type CatalogBadgeKey,
  type SiteCatalogOptions,
} from "@chaiberi/shared";
import { useState } from "react";

import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { FieldHint, SettingsGroup } from "../../ui/settings-groups";
import { setBadgeLabel } from "../../../lib/site-view/catalog-form";

interface Props {
  options: SiteCatalogOptions;
  onChange: (options: SiteCatalogOptions) => void;
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** «1 своя», «2 своих» — сводка свёрнутой группы. */
const customWord = (n: number) => (n % 10 === 1 && n % 100 !== 11 ? `${n} своя` : `${n} своих`);

export function BadgeLabelsGroup({ options, onChange }: Props) {
  // Набранное, в том числе недопустимое (в черновик оно не уходит).
  const [typed, setTyped] = useState<Partial<Record<CatalogBadgeKey, string>>>({});
  const custom = Object.keys(options.badgeLabels).length;
  const valueOf = (key: CatalogBadgeKey) => typed[key] ?? options.badgeLabels[key] ?? "";
  const issueOf = (key: CatalogBadgeKey) => (valueOf(key).trim() === "" ? null : badgeLabelIssue(valueOf(key)));
  const hasIssue = CATALOG_BADGE_KEYS.some((key) => issueOf(key) !== null);

  function change(key: CatalogBadgeKey, value: string) {
    setTyped((prev) => ({ ...prev, [key]: value }));
    const next = setBadgeLabel(options, key, value);
    if (next) onChange(next);
  }

  return (
    <SettingsGroup
      id="badges"
      title="Подписи бейджей"
      tocLabel="Бейджи"
      hint="Свои слова на бейджах вместо подписей платформы."
      summary={hasIssue ? "нужно исправить" : custom > 0 ? customWord(custom) : "по умолчанию"}
      status={hasIssue ? "error" : undefined}
    >
      <FieldHint className="mt-0">
        Пусто — подпись платформы. Нельзя превосходные и рейтинговые слова: {BADGE_LABEL_STOP_WORDS.map((w) => `«${w}»`).join(", ")} — закон о рекламе.
      </FieldHint>
      {CATALOG_BADGE_KEYS.map((key) => {
        const value = valueOf(key);
        const issue = issueOf(key);
        const id = `badge-label-${key}`;
        return (
          <div key={key} className="flex flex-col gap-1">
            <Label htmlFor={id}>{capital(CATALOG_BADGE_DEFAULT_LABELS[key])}</Label>
            <Input
              id={id}
              value={value}
              maxLength={BADGE_LABEL_MAX + 10}
              placeholder={CATALOG_BADGE_DEFAULT_LABELS[key]}
              aria-invalid={issue ? true : undefined}
              aria-describedby={issue ? `${id}-issue` : undefined}
              onChange={(e) => change(key, e.target.value)}
            />
            {issue ? (
              <p id={`${id}-issue`} className="type-caption text-destructive">
                {issue}. На сайте пока прежняя подпись.
              </p>
            ) : null}
          </div>
        );
      })}
    </SettingsGroup>
  );
}
